
/**
 * Popular-search landing pages. Scope for every page: active RO listings in the
 * cars + SUV categories (vans excluded). Filters live server-side in
 * get_landing_page_* (fuel_type / transmission columns, price converted to EUR).
 */
export const LANDING_SLUGS = [
  "masini-sub-5000-euro",
  "masini-sub-10000-euro",
  "masini-sub-15000-euro",
  "masini-automate",
  "masini-diesel",
  "masini-hibrid",
] as const;
export type LandingSlug = (typeof LANDING_SLUGS)[number];

export const LANDING_INDEX_MIN = 10;
export const LANDING_PAGE_SIZE = 24;
export const ORIGIN = "https://roautomarket.ro";

export type LandingConfig = {
  slug: LandingSlug;
  title: string;
  description: string;
  /** Short label for link lists. */
  label: string;
  /** Phrase used inside sentences, e.g. "cu cutie automată". */
  phrase: string;
};

export const LANDING_PAGES: Record<LandingSlug, LandingConfig> = {
  "masini-sub-5000-euro": {
    slug: "masini-sub-5000-euro",
    title: "Mașini second hand sub 5.000 euro",
    description: "Anunțuri cu mașini second hand până la 5.000 euro, de la dealeri și vânzători privați din România.",
    label: "Sub 5.000 euro",
    phrase: "până la 5.000 euro",
  },
  "masini-sub-10000-euro": {
    slug: "masini-sub-10000-euro",
    title: "Mașini second hand sub 10.000 euro",
    description: "Anunțuri cu mașini second hand până la 10.000 euro, de la dealeri și vânzători privați din România.",
    label: "Sub 10.000 euro",
    phrase: "până la 10.000 euro",
  },
  "masini-sub-15000-euro": {
    slug: "masini-sub-15000-euro",
    title: "Mașini second hand sub 15.000 euro",
    description: "Anunțuri cu mașini second hand până la 15.000 euro, de la dealeri și vânzători privați din România.",
    label: "Sub 15.000 euro",
    phrase: "până la 15.000 euro",
  },
  "masini-automate": {
    slug: "masini-automate",
    title: "Mașini second hand cu cutie automată",
    description: "Anunțuri cu mașini second hand cu cutie automată, de la dealeri și vânzători privați din România.",
    label: "Cutie automată",
    phrase: "cu cutie automată",
  },
  "masini-diesel": {
    slug: "masini-diesel",
    title: "Mașini second hand diesel",
    description: "Anunțuri cu mașini second hand diesel, de la dealeri și vânzători privați din România.",
    label: "Diesel",
    phrase: "diesel",
  },
  "masini-hibrid": {
    slug: "masini-hibrid",
    title: "Mașini second hand hibrid",
    description: "Anunțuri cu mașini second hand hibrid și hibrid plug-in, de la dealeri și vânzători privați din România.",
    label: "Hibrid",
    phrase: "hibrid (inclusiv plug-in)",
  },
};

export type LandingStats = { count: number; median_eur: number | null; top_makes: Array<{ make: string; count: number }> };

/** Index only with at least LANDING_INDEX_MIN active listings; unknown count stays noindex. */
export const landingRobots = (count: number | null | undefined) =>
  typeof count === "number" && count >= LANDING_INDEX_MIN ? "index, follow" : "noindex, follow";

export const landingInSitemap = (count: number | null | undefined) => landingRobots(count) === "index, follow";

export const landingCanonical = (slug: LandingSlug, page = 1) => `${ORIGIN}/${slug}${page > 1 ? `?page=${page}` : ""}`;

export const brandSlug = (make: string) =>
  make.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

export const formatEur = (n: number) => `${new Intl.NumberFormat("ro-RO", { maximumFractionDigits: 0 }).format(n)} EUR`;

export type Faq = { question: string; answer: string };

/** Visible FAQ and FAQPage JSON-LD both come from this one function. */
export function landingFaqs(cfg: LandingConfig, stats: LandingStats): Faq[] {
  const n = stats.count.toLocaleString("ro-RO");
  const faqs: Faq[] = [
    {
      question: `Câte mașini second hand ${cfg.phrase} sunt acum pe RO Auto Market?`,
      answer: `Acum sunt ${n} anunțuri active cu autoturisme și SUV-uri ${cfg.phrase} în România. Numărul se calculează din anunțurile active.`,
    },
  ];
  if (stats.median_eur != null) {
    faqs.push({
      question: `Care este prețul median al mașinilor ${cfg.phrase}?`,
      answer: `Prețul median este ${formatEur(stats.median_eur)}, calculat din anunțurile active cu preț. Prețurile în lei sunt convertite în euro.`,
    });
  }
  if (stats.top_makes.length) {
    faqs.push({
      question: `Ce mărci apar cel mai des?`,
      answer: `${stats.top_makes.map((m) => `${m.make} (${m.count.toLocaleString("ro-RO")})`).join(", ")}.`,
    });
  }
  faqs.push({
    question: "Cum iau legătura cu vânzătorul?",
    answer:
      "Din pagina anunțului suni sau scrii direct vânzătorului, fără comision pentru cumpărători. La dealerii marcați ca verificați, firma este verificată la ANAF și RISCO; starea mașinii nu este verificată.",
  });
  return faqs;
}

